import * as Yup from "yup";
import i18n, { t } from "i18next";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import { jwtDecode } from "jwt-decode";
import Button from "@mui/material/Button";
import PhoneInput from "react-phone-input-2";
import Snackbar from "@mui/material/Snackbar";
import Checkbox from "@mui/material/Checkbox";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import { GoogleLogin } from "@react-oauth/google";
import BookingApi from "../../../API/Apis/BookingApi";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatTheme } from "../../../config/MuiColorThemes";
import MyReservationsPage from "../../../views/reservations";
import FormControlLabel from "@mui/material/FormControlLabel";
import { CircularProgress, FormControl } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import NotAuthorisedPage from "../../../views/notAuthorisedPage";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AppSettingResponse } from "../../../@types/DTO/AppSetting";
import { Divider, FormHelperText, Typography } from "@mui/material";
import { BookingGetters } from "../../../store/onlineBooking/getters";
import { useBackgroundLoader } from "../../UI/BgReservedArea/BackgroundLoader";
import { PersonOutlined, BusinessOutlined } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

export default function SignUp() {
  const navigate = useNavigate();
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const primaryColor = dynamicTheme?.palette?.primary?.main || "#2cb133";

  const appSettings = BookingGetters.getAppSettings();
  const allowPersonalOnly = !!appSettings?.allowPersonalAccountRegistrationOnly;
  const allowCompanyOnly = !!appSettings?.allowCompanyAccountRegistrationOnly;

  const [accountType, setAccountType] = useState<null | "personal" | "company">(
    allowCompanyOnly && allowPersonalOnly
      ? null
      : allowCompanyOnly
      ? "company"
      : allowPersonalOnly
      ? "personal"
      : null
  );
  const [companyStep, setCompanyStep] = useState<number>(1);
  const [companyData, setCompanyData] = useState({
    companyName: "",
    vatNumber: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
    acceptPrivacy: false,
    acceptPromo: false,
  });
  const [companyErrors, setCompanyErrors] = useState<Record<string, string>>(
    {}
  );

  const [step, setStep] = useState<number>(1);
  const [errorToast, setErrorToast] = useState({
    open: false,
    message: "",
    type: "",
  });
  const isCompanySubmitting = useRef<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [termsFileUrl, setTermsFileUrl] = useState<string>("");
  const [privacyFileUrl, setPrivacyFileUrl] = useState<string>("");
  const [personalAcceptPrivacy, setPersonalAcceptPrivacy] =
    useState<boolean>(false);

  const [showReserve, setShowReserve] = useState<boolean>(
    !!appSettings?.reservedArea
  );

  const ageValidation = new Date();
  ageValidation.setFullYear(ageValidation.getFullYear() - 18);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t("First Name is required")),
    lastName: Yup.string().required(t("Last Name is required")),
    phoneNumber: Yup.string()
      .required(t("Phone Number is required"))
      .min(10, t("Phone Number must be at least 10 digits"))
      .max(15, t("Phone Number must be at most 15 digits")),
    email: Yup.string()
      .required(t("Email is required"))
      .email(t("Email is invalid")),
    password: Yup.string()
      .required(t("Password is required"))
      .min(6, t("Password must be at least 6 characters")),
    confirmPassword: Yup.string()
      .required(t("Confirm Password is required"))
      .oneOf([Yup.ref("password"), null], t("Confirm Password does not match")),
    birthDate: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .required(t("Birth Date is required"))
      .max(ageValidation, t("Age must be at least 18 years old")),
    acceptTerms: Yup.bool().oneOf([true], t("Accept Terms is required")),
  });

  const reservedAreaBgImageUrl = appSettings?.config?.reservedAreaBgImage;
  const defaultImage = "/assets/images/reserve_area_car.jpg";
  const { getImage, backgroundStyle } = useBackgroundLoader({
    imageUrl: reservedAreaBgImageUrl,
    defaultImage,
  });

  const {
    register,
    handleSubmit,
    trigger,
    control,
    setError,
    clearErrors,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    shouldUnregister: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      birthDate: null,
      acceptTerms: false,
    },
  });

  const nextStep = async (): Promise<void> => {
    if (step === 1) {
      const valid = await trigger(["firstName", "lastName", "phoneNumber"]);
      if (valid) {
        clearErrors(["email", "password", "confirmPassword"]);
        setStep(2);
      }
    } else if (step === 2) {
      const valid = await trigger(["email", "password", "confirmPassword"]);
      if (valid) {
        clearErrors(["birthDate", "acceptTerms"]);
        setStep(3);
      }
    }
  };
  const sendConfirmationEmailAfterRegistration =
    appSettings?.sendConfirmationEmailAfterRegistration;
  const requireManualApprovalForNewRegistrations =
    appSettings?.requireManualApprovalForNewRegistrations;
  const createCrmAfterOnlineUserRegistration =
    appSettings?.createCrmAfterOnlineUserRegistration;
  const language = i18n.language;
  const sendRegistrationEmail = (onlineUserId: string) => {
    if (!sendConfirmationEmailAfterRegistration || !language) return;
    const emailKey = requireManualApprovalForNewRegistrations
      ? "registrationRequestEmail"
      : "registrationEmail";
    BookingApi.sendRegistrationEmail(onlineUserId, emailKey, language).catch(
      (err) => {
        console.error("Failed to send email:", err);
      }
    );
  };

  const onSubmit = async (formData: any): Promise<void> => {
    if (step !== 3) return;

    const isValid = await trigger(["birthDate", "acceptTerms"]);
    if (!isValid || !personalAcceptPrivacy) return;

    try {
      setLoader(true);

      const response = await BookingApi.createNewUser(
        formData,
        requireManualApprovalForNewRegistrations
      );
      const result = response.data;
      <MyReservationsPage ids={result.id} />;
      if (result.status) {
        if (requireManualApprovalForNewRegistrations) {
          localStorage.setItem("isUserLoggedin", "false");
        } else {
          localStorage.setItem("userID", result.id);
          localStorage.setItem("isUserLoggedin", "true");
          if (createCrmAfterOnlineUserRegistration) {
            try {
              const crmResponse = await BookingApi.createOnlineuserCustomer({
                onlineUserID: result.id,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                mobileNumber: formData.phoneNumber,
                birthDate: formData.birthDate,
                isDriver: false,
                isSupplier: false,
                isPhysicalPerson: true,
                isClient: true,
              });
              if (crmResponse?.data?.id) {
                localStorage.setItem("customerId", crmResponse.data.id);
              }
            } catch (crmErr) {
              console.error("Personal CRM creation failed:", crmErr);
            }
          }
        }
        sendRegistrationEmail(result.id);
        navigate("/SignUpSuccess");
      } else {
        setErrorToast({
          open: true,
          message: result.message,
          type: "error",
        });

        if (result.message === "Email already exist") {
          setError("email", {
            type: "manual",
            message: t("Email already exist"),
          });
          setStep(2);
        }

        if (result.message === "Mobile number already exist") {
          setError("phoneNumber", {
            type: "manual",
            message: t("Phone Number already exist"),
          });
          setStep(1);
        }
      }
    } catch (error) {
      console.error(error);
      setErrorToast({
        open: true,
        message: "Something went wrong! Try again.",
        type: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    BookingApi.getPrivacyAndTerms().then((e) => {
      setTermsFileUrl(e?.data?.termsConditionFile || "");
      setPrivacyFileUrl(e?.data?.privacyfileUrl || "");
    });
  }, []);

  const openFile = (url: string) => {
    if (!url) return;
    if (url.includes("s3.amazonaws")) {
      fetch(url).then(async (e) => {
        const fileBlob = await e.blob();
        const linkObj = document.createElement("a");
        linkObj.href = URL.createObjectURL(fileBlob);
        linkObj.target = "_blank";
        linkObj.click();
      });
    } else {
      const linkObj = document.createElement("a");
      linkObj.href = url;
      linkObj.target = "_blank";
      linkObj.click();
    }
  };

  // ── Company flow helpers ──────────────────────────────────────────────────
  const validateCompanyStep1 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!companyData.companyName.trim())
      errs.companyName = t("Company Name is required");
    if (!companyData.vatNumber.trim())
      errs.vatNumber = t("VAT number is required");
    if (!companyData.phoneNumber.trim())
      errs.phoneNumber = t("Phone Number is required");
    else if (companyData.phoneNumber.length < 10)
      errs.phoneNumber = t("Phone Number must be at least 10 digits");
    else if (companyData.phoneNumber.length > 15)
      errs.phoneNumber = t("Phone Number must be at most 15 digits");
    setCompanyErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateCompanyStep2 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!companyData.email.trim()) errs.email = t("Email is required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email))
      errs.email = t("Email is invalid");
    if (!companyData.password) errs.password = t("Password is required");
    else if (companyData.password.length < 6)
      errs.password = t("Password must be at least 6 characters");
    if (!companyData.confirmPassword)
      errs.confirmPassword = t("Confirm Password is required");
    else if (companyData.confirmPassword !== companyData.password)
      errs.confirmPassword = t("Confirm Password does not match");
    setCompanyErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCompanyNext = (): void => {
    if (companyStep === 1 && validateCompanyStep1()) {
      setCompanyErrors({});
      setCompanyStep(2);
    } else if (companyStep === 2 && validateCompanyStep2()) {
      setCompanyErrors({});
      setCompanyStep(3);
    }
  };

  const handleCompanySubmit = async (): Promise<void> => {
    if (isCompanySubmitting.current) return;
    if (!companyData.acceptTerms || !companyData.acceptPrivacy) return;
    isCompanySubmitting.current = true;
    try {
      setLoader(true);
      const response = await BookingApi.createNewUser(
        {
          companyName: companyData.companyName,
          vatNumber: companyData.vatNumber,
          phoneNumber: companyData.phoneNumber,
          email: companyData.email,
          password: companyData.password,
          isPhysicalPerson: false,
        },
        requireManualApprovalForNewRegistrations
      );
      const result = response.data;
      if (!result) throw new Error("Empty response from server");
      if (result.status) {
        if (requireManualApprovalForNewRegistrations) {
          localStorage.setItem("isUserLoggedin", "false");
        } else {
          localStorage.setItem("userID", result.id);
          localStorage.setItem("isUserLoggedin", "true");
          if (createCrmAfterOnlineUserRegistration) {
            try {
              const crmPayload = {
                onlineUserID: result.id,
                companyName: companyData.companyName,
                vatNumber: companyData.vatNumber,
                mobileNumber: companyData.phoneNumber,
                telephone: companyData.phoneNumber,
                email: companyData.email,
                customerType: "COMPANY",
                isPhysicalPerson: false,
                isClient: true,
                isSupplier: false,
                isDriver: false,
              };

              const crmResponse = await BookingApi.createOnlineuserCustomer(
                crmPayload
              );

              if (crmResponse?.data?.id) {
                localStorage.setItem("customerId", crmResponse.data.id);
              }
            } catch (err) {
              console.error("Company CRM creation failed:", err);
            }
          }
        }

        sendRegistrationEmail(result.id);
        navigate("/SignUpSuccess");
      } else {
        setErrorToast({ open: true, message: result.message, type: "error" });
        if (result.message === "Email already exist") {
          setCompanyErrors({ email: t("Email already exist") });
          setCompanyStep(2);
        }
      }
    } catch (error) {
      console.error(error);
      setErrorToast({
        open: true,
        message: "Something went wrong! Try again.",
        type: "error",
      });
    } finally {
      setLoader(false);
      isCompanySubmitting.current = false;
    }
  };

  const handleCompanyBack = (): void => {
    if (companyStep === 1) {
      if (!allowCompanyOnly || allowPersonalOnly) setAccountType(null);
      else navigate("/signin");
    } else {
      setCompanyStep(companyStep - 1);
    }
  };

  // ── Shared title logic ────────────────────────────────────────────────────
  const getTitle = (): string => {
    if (accountType === null) return t("Create your account");
    if (accountType === "company") {
      if (companyStep === 1) return t("Create your account");
      if (companyStep === 2) return t("Set your login details");
      return t("Final step");
    }
    // personal
    if (step === 1) return t("Create your account");
    if (step === 2) return t("Set your login details");
    return t("Final step");
  };

  useEffect(() => {
    BookingApi.getAppSetting().then((e: AppSettingResponse) => {
      setShowReserve(e.reservedArea);
      if (
        e.allowCompanyAccountRegistrationOnly &&
        e.allowPersonalAccountRegistrationOnly
      ) {
        setAccountType(null);
      } else if (e.allowCompanyAccountRegistrationOnly) {
        setAccountType("company");
      } else if (e.allowPersonalAccountRegistrationOnly) {
        setAccountType("personal");
      }
    });
  }, []);

  const handleGoogleSuccess = async (
    credentialResponse: any
  ): Promise<void> => {
    try {
      setLoader(true);
      const decoded: any = jwtDecode(credentialResponse.credential);
      const googleUserData = {
        firstName: decoded.given_name || "",
        lastName: decoded.family_name || "",
        email: decoded.email?.toLowerCase(),
        phoneNumber: "",
        password: decoded.sub,
        birthDate: "",
        onlineUserType: 3,
        onlineUserRole: 2,
        onlineUserStatus: 1,
      };

      const response = await BookingApi.createNewUser(
        googleUserData,
        requireManualApprovalForNewRegistrations
      );
      <MyReservationsPage ids={response.data.id} />;

      if (response.data.status) {
        localStorage.setItem("userID", response.data.id);
        localStorage.setItem("isUserLoggedin", "true");
        navigate("/SignUpSuccess");
      } else {
        setErrorToast({
          open: true,
          message: response.data.message,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Google Sign-In error:", error);
      setErrorToast({
        open: true,
        message: "Google Sign-In failed. Please try again.",
        type: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const fieldSx = { mt: 2, width: "100%", backgroundColor: "white" };
  const btnSx = {
    mt: 3,
    mb: 2,
    fontWeight: "bold",
    fontSize: "17px",
    p: 1,
    textTransform: "none",
  };
  const backLinkSx = {
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "100%",
    letterSpacing: 0,
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      {loader && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <CircularProgress sx={{ color: "white" }} />
        </div>
      )}
      <div className="relative min-h-screen flex items-center justify-center bg-transparent">
        {getImage && (
          <div
            style={backgroundStyle}
            className="absolute inset-0 w-full h-full object-cover lg:hidden z-0"
          />
        )}
        {showReserve ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-screen">
            {/* LEFT SECTION */}
            <div className="hidden lg:block w-full h-screen overflow-hidden">
              {!getImage ? (
                <div className="absolute top-0 left-0 w-1/2 h-full flex items-center justify-center z-10">
                  <CircularProgress sx={{ color: "gray" }} />
                </div>
              ) : (
                <div className="w-full h-full object-cover">
                  <div
                    style={backgroundStyle}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            {/* RIGHT SECTION */}
            <div className="relative z-10 flex justify-center items-center max-h-screen lg:bg-gray-100">
              <div className="bg-white shadow-lg rounded-2xl px-9 py-11 w-full max-w-[22rem] md:max-w-[25rem] mx-4 border-2">
                <h1
                  className="text-center text-2xl font-semibold mb-6"
                  style={{ color: primaryColor }}
                >
                  {getTitle()}
                </h1>

                {/* ── ACCOUNT TYPE SELECTION ─────────────────────────────── */}
                {accountType === null && (
                  <Container component="main" disableGutters>
                    <p className="text-center text-sm text-gray-500 mb-6">
                      {t("Select your account type to get started.")}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <button
                        onClick={() => {
                          setAccountType("personal");
                          setStep(1);
                          setCompanyData({
                            companyName: "",
                            vatNumber: "",
                            phoneNumber: "",
                            email: "",
                            password: "",
                            confirmPassword: "",
                            acceptTerms: false,
                            acceptPrivacy: false,
                            acceptPromo: false,
                          });
                          setCompanyErrors({});
                          setCompanyStep(1);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "14px",
                          padding: "16px 20px",
                          borderRadius: "10px",
                          border: `2px solid #e5e7eb`,
                          background: "#fff",
                          cursor: "pointer",
                          fontSize: "15px",
                          fontWeight: 500,
                          color: "#222",
                          transition: "border-color 0.15s",
                          width: "100%",
                          textAlign: "left",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = primaryColor)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.borderColor = "#e5e7eb")
                        }
                      >
                        <PersonOutlined
                          style={{ color: primaryColor, fontSize: "28px" }}
                        />
                        <span>
                          <span style={{ display: "block", fontWeight: 600 }}>
                            {t("Personal")}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#9ca3af",
                            }}
                          >
                            {t("Individual account")}
                          </span>
                        </span>
                      </button>

                      <button
                        onClick={() => {
                          setAccountType("company");
                          setCompanyStep(1);
                          reset();
                          setStep(1);
                          setPersonalAcceptPrivacy(false);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "14px",
                          padding: "16px 20px",
                          borderRadius: "10px",
                          border: `2px solid #e5e7eb`,
                          background: "#fff",
                          cursor: "pointer",
                          fontSize: "15px",
                          fontWeight: 500,
                          color: "#222",
                          transition: "border-color 0.15s",
                          width: "100%",
                          textAlign: "left",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = primaryColor)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.borderColor = "#e5e7eb")
                        }
                      >
                        <BusinessOutlined
                          style={{ color: primaryColor, fontSize: "28px" }}
                        />
                        <span>
                          <span style={{ display: "block", fontWeight: 600 }}>
                            {t("Company")}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#9ca3af",
                            }}
                          >
                            {t("Business account with VAT")}
                          </span>
                        </span>
                      </button>
                    </div>

                    <Divider sx={{ bgcolor: "grey.300", mt: 4, mb: 3 }} />

                    <Grid container direction="column" alignItems="center">
                      <Grid item>
                        <Link
                          onClick={() => navigate("/signin")}
                          component="button"
                          underline="none"
                          sx={backLinkSx}
                        >
                          {t("Back")}
                        </Link>
                      </Grid>
                    </Grid>
                  </Container>
                )}

                {/* ── COMPANY FLOW ───────────────────────────────────────── */}
                {accountType === "company" && (
                  <Container component="main" disableGutters>
                    {companyStep === 3 && (
                      <div className="text-start text-[#000000] mt-4 mb-4 mx-6">
                        {t(
                          "Confirm your details and accept the Terms & Conditions to finish."
                        )}
                      </div>
                    )}
                    <Box
                      sx={{
                        width: "100%",
                        "& .MuiTextField-root": fieldSx,
                      }}
                    >
                      {/* Company step 1: Company details */}
                      {companyStep === 1 && (
                        <>
                          <TextField
                            required
                            label={t("Company Name")}
                            value={companyData.companyName}
                            onChange={(e) => {
                              setCompanyData({
                                ...companyData,
                                companyName: e.target.value,
                              });
                              if (companyErrors.companyName)
                                setCompanyErrors((prev) => ({
                                  ...prev,
                                  companyName: "",
                                }));
                            }}
                            error={!!companyErrors.companyName}
                            helperText={companyErrors.companyName}
                            FormHelperTextProps={{
                              sx: { textAlign: "left", ml: 0 },
                            }}
                          />
                          <TextField
                            required
                            label={t("VAT number")}
                            value={companyData.vatNumber}
                            onChange={(e) => {
                              setCompanyData({
                                ...companyData,
                                vatNumber: e.target.value,
                              });
                              if (companyErrors.vatNumber)
                                setCompanyErrors((prev) => ({
                                  ...prev,
                                  vatNumber: "",
                                }));
                            }}
                            error={!!companyErrors.vatNumber}
                            helperText={companyErrors.vatNumber}
                            FormHelperTextProps={{
                              sx: { textAlign: "left", ml: 0 },
                            }}
                          />
                          <div className="mt-5">
                            <PhoneInput
                              country="it"
                              specialLabel={
                                <span>
                                  {t("Phone Number")}
                                  <span style={{ color: "red" }}> *</span>
                                </span>
                              }
                              value={companyData.phoneNumber}
                              onChange={(val) => {
                                setCompanyData({
                                  ...companyData,
                                  phoneNumber: val,
                                });
                                if (companyErrors.phoneNumber)
                                  setCompanyErrors((prev) => ({
                                    ...prev,
                                    phoneNumber: "",
                                  }));
                              }}
                              dropdownClass="mx-4"
                              style={{
                                color: companyErrors.phoneNumber
                                  ? "#d32f2f"
                                  : "#736969",
                              }}
                              inputStyle={{
                                height: "100%",
                                width: "100%",
                                color: "black",
                                borderWidth: "2px",
                                borderColor: companyErrors.phoneNumber
                                  ? "#d32f2f"
                                  : "#ccc",
                              }}
                            />
                            {companyErrors.phoneNumber && (
                              <Typography variant="caption" color="error">
                                {companyErrors.phoneNumber}
                              </Typography>
                            )}
                          </div>
                          <Button
                            fullWidth
                            variant="contained"
                            sx={btnSx}
                            onClick={handleCompanyNext}
                          >
                            {t("Next")}
                          </Button>
                        </>
                      )}

                      {/* Company step 2: Login credentials */}
                      {companyStep === 2 && (
                        <>
                          <TextField
                            required
                            label={t("Email_Address")}
                            value={companyData.email}
                            onChange={(e) => {
                              setCompanyData({
                                ...companyData,
                                email: e.target.value,
                              });
                              if (companyErrors.email)
                                setCompanyErrors((prev) => ({
                                  ...prev,
                                  email: "",
                                }));
                            }}
                            error={!!companyErrors.email}
                            helperText={companyErrors.email}
                            FormHelperTextProps={{
                              sx: { textAlign: "left", ml: 0 },
                            }}
                          />
                          <TextField
                            required
                            label={t("Password")}
                            type="password"
                            value={companyData.password}
                            onChange={(e) => {
                              setCompanyData({
                                ...companyData,
                                password: e.target.value,
                              });
                              if (companyErrors.password)
                                setCompanyErrors((prev) => ({
                                  ...prev,
                                  password: "",
                                }));
                            }}
                            error={!!companyErrors.password}
                            helperText={companyErrors.password}
                            FormHelperTextProps={{
                              sx: { textAlign: "left", ml: 0 },
                            }}
                          />
                          <TextField
                            required
                            label={t("Confirm Password")}
                            type="password"
                            value={companyData.confirmPassword}
                            onChange={(e) => {
                              setCompanyData({
                                ...companyData,
                                confirmPassword: e.target.value,
                              });
                              if (companyErrors.confirmPassword)
                                setCompanyErrors((prev) => ({
                                  ...prev,
                                  confirmPassword: "",
                                }));
                            }}
                            error={!!companyErrors.confirmPassword}
                            helperText={companyErrors.confirmPassword}
                            FormHelperTextProps={{
                              sx: { textAlign: "left", ml: 0 },
                            }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            sx={btnSx}
                            onClick={handleCompanyNext}
                          >
                            {t("Next")}
                          </Button>
                        </>
                      )}

                      {/* Company step 3: Final step */}
                      {companyStep === 3 && (
                        <>
                          <FormControl
                            error={!companyData.acceptTerms}
                            sx={{
                              mt: "18px",
                              alignItems: "flex-start",
                              width: "100%",
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={companyData.acceptTerms}
                                  onChange={(e) =>
                                    setCompanyData({
                                      ...companyData,
                                      acceptTerms: e.target.checked,
                                    })
                                  }
                                />
                              }
                              label={
                                <span>
                                  {t("I accept the ")}{" "}
                                  <span
                                    style={{
                                      textDecoration: "underline",
                                      cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      openFile(termsFileUrl);
                                    }}
                                  >
                                    {t("Terms & Conditions")}
                                  </span>
                                </span>
                              }
                            />
                          </FormControl>

                          <FormControl
                            error={!companyData.acceptPrivacy}
                            sx={{
                              mt: "4px",
                              alignItems: "flex-start",
                              width: "100%",
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={companyData.acceptPrivacy}
                                  onChange={(e) =>
                                    setCompanyData({
                                      ...companyData,
                                      acceptPrivacy: e.target.checked,
                                    })
                                  }
                                />
                              }
                              label={
                                <span>
                                  {t("I accept the ")}{" "}
                                  <span
                                    style={{
                                      textDecoration: "underline",
                                      cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      openFile(privacyFileUrl);
                                    }}
                                  >
                                    {t("Privacy Policy")}
                                  </span>
                                </span>
                              }
                            />
                          </FormControl>

                          <FormControlLabel
                            sx={{
                              mt: "4px",
                              alignItems: "flex-start",
                            }}
                            control={
                              <Checkbox
                                sx={{
                                  paddingTop: "2px",
                                }}
                                checked={companyData.acceptPromo}
                                onChange={(e) =>
                                  setCompanyData({
                                    ...companyData,
                                    acceptPromo: e.target.checked,
                                  })
                                }
                              />
                            }
                            label={t(
                              "I agree to receive promotional communications"
                            )}
                          />

                          <Button
                            fullWidth
                            variant="contained"
                            sx={btnSx}
                            disabled={
                              !companyData.acceptTerms ||
                              !companyData.acceptPrivacy ||
                              loader
                            }
                            onClick={handleCompanySubmit}
                          >
                            {t("Sign up")}
                          </Button>
                        </>
                      )}

                      <Divider sx={{ bgcolor: "grey.300", mt: 2, mb: 4 }} />

                      <Grid container direction="column" alignItems="center">
                        <Grid item>
                          <Link
                            onClick={handleCompanyBack}
                            component="button"
                            underline="none"
                            sx={backLinkSx}
                          >
                            {t("Back")}
                          </Link>
                        </Grid>
                      </Grid>
                    </Box>
                  </Container>
                )}

                {/* ── PERSONAL FLOW (existing, unchanged) ───────────────── */}
                {accountType === "personal" && (
                  <>
                    {step === 3 && (
                      <div className="text-start text-[#000000] mt-4 mb-4 mx-6">
                        {t(
                          "Confirm your details and accept the Terms & Conditions to finish."
                        )}
                      </div>
                    )}
                    <Container component="main" disableGutters>
                      <Grid container justifyContent="center">
                        <Box
                          component="form"
                          onSubmit={handleSubmit(onSubmit)}
                          noValidate
                          sx={{
                            width: "100%",
                            "& .MuiTextField-root": {
                              mt: 2,
                              width: "100%",
                              backgroundColor: "white",
                            },
                          }}
                        >
                          {/* STEP 1 */}
                          {step === 1 && (
                            <>
                              <TextField
                                required
                                label={t("First Name")}
                                {...register("firstName")}
                                error={!!errors.firstName}
                                helperText={errors.firstName?.message}
                                FormHelperTextProps={{
                                  sx: { textAlign: "left", ml: 0 },
                                }}
                              />
                              <TextField
                                required
                                label={t("Last Name")}
                                {...register("lastName")}
                                error={!!errors.lastName}
                                helperText={errors.lastName?.message}
                                FormHelperTextProps={{
                                  sx: { textAlign: "left", ml: 0 },
                                }}
                              />
                              <div className="mt-5">
                                <Controller
                                  name="phoneNumber"
                                  control={control}
                                  rules={{
                                    required: t("Phone Number is required"),
                                  }}
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <>
                                      <PhoneInput
                                        country="it"
                                        specialLabel={
                                          <span>
                                            {t("Phone Number")}
                                            <span style={{ color: "red" }}>
                                              {" "}
                                              *
                                            </span>
                                          </span>
                                        }
                                        value={value}
                                        onChange={onChange}
                                        dropdownClass="mx-4"
                                        style={{
                                          color: error ? "#d32f2f" : "#736969",
                                        }}
                                        inputStyle={{
                                          height: "100%",
                                          width: "100%",
                                          color: "black",
                                          borderWidth: "2px",
                                          borderColor: error
                                            ? "#d32f2f"
                                            : "#ccc",
                                        }}
                                      />
                                      {error && (
                                        <Typography
                                          variant="caption"
                                          color="error"
                                        >
                                          {error.message}
                                        </Typography>
                                      )}
                                    </>
                                  )}
                                />
                              </div>
                              <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={btnSx}
                                onClick={nextStep}
                              >
                                {t("Next")}
                              </Button>
                            </>
                          )}

                          {/* STEP 2 */}
                          {step === 2 && (
                            <>
                              <TextField
                                required
                                label={t("Email_Address")}
                                {...register("email")}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                FormHelperTextProps={{
                                  sx: { textAlign: "left", ml: 0 },
                                }}
                              />
                              <TextField
                                required
                                label={t("Password")}
                                type="password"
                                {...register("password")}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                FormHelperTextProps={{
                                  sx: { textAlign: "left", ml: 0 },
                                }}
                              />
                              <TextField
                                required
                                label={t("Confirm Password")}
                                type="password"
                                {...register("confirmPassword")}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                                FormHelperTextProps={{
                                  sx: { textAlign: "left", ml: 0 },
                                }}
                              />
                              <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={btnSx}
                                onClick={nextStep}
                              >
                                {t("Next")}
                              </Button>
                            </>
                          )}

                          {/* STEP 3 */}
                          {step === 3 && (
                            <>
                              <Controller
                                name="birthDate"
                                control={control}
                                render={({ field }) => (
                                  <LocalizationProvider
                                    dateAdapter={AdapterDateFns}
                                  >
                                    <DesktopDatePicker
                                      label={t("Date of birth")}
                                      inputFormat="dd/MM/yyyy"
                                      value={field.value || null}
                                      maxDate={new Date("3000-12-31")}
                                      onChange={(date) =>
                                        setValue("birthDate", date as any, {
                                          shouldValidate: true,
                                        })
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          required
                                          fullWidth
                                          error={!!errors.birthDate}
                                          helperText={errors.birthDate?.message}
                                          InputLabelProps={{
                                            ...params.InputLabelProps,
                                            shrink: true,
                                          }}
                                          FormHelperTextProps={{
                                            sx: { textAlign: "left", ml: 0 },
                                          }}
                                        />
                                      )}
                                    />
                                  </LocalizationProvider>
                                )}
                              />
                              <FormControl
                                error={!!errors.acceptTerms}
                                sx={{ mt: "18px", alignItems: "flex-start" }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      {...register("acceptTerms")}
                                      checked={!!watch("acceptTerms")}
                                    />
                                  }
                                  label={
                                    <span>
                                      {t("I accept the ")}{" "}
                                      <span
                                        style={{
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openFile(termsFileUrl);
                                        }}
                                      >
                                        {t("Terms & Conditions")}
                                      </span>
                                    </span>
                                  }
                                />
                                {errors.acceptTerms && (
                                  <FormHelperText
                                    sx={{
                                      mt: 0,
                                      ml: 0,
                                      textAlign: "left",
                                      width: "100%",
                                    }}
                                  >
                                    {errors.acceptTerms.message}
                                  </FormHelperText>
                                )}
                              </FormControl>
                              <FormControl
                                error={!personalAcceptPrivacy}
                                sx={{ mt: "4px", alignItems: "flex-start" }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={personalAcceptPrivacy}
                                      onChange={(e) =>
                                        setPersonalAcceptPrivacy(
                                          e.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label={
                                    <span>
                                      {t("I accept the ")}{" "}
                                      <span
                                        style={{
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openFile(privacyFileUrl);
                                        }}
                                      >
                                        {t("Privacy Policy")}
                                      </span>
                                    </span>
                                  }
                                />
                              </FormControl>
                              <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={btnSx}
                                disabled={!personalAcceptPrivacy}
                                onClick={handleSubmit(onSubmit)}
                              >
                                {t("Sign up")}
                              </Button>
                            </>
                          )}

                          <Divider sx={{ bgcolor: "grey.300", mt: 2, mb: 4 }} />

                          <Grid
                            container
                            direction="column"
                            alignItems="center"
                          >
                            <Grid item>
                              <Link
                                onClick={() => {
                                  if (step > 1) {
                                    setStep(step - 1);
                                  } else if (
                                    !allowPersonalOnly ||
                                    allowCompanyOnly
                                  ) {
                                    setAccountType(null);
                                  } else {
                                    navigate("/signin");
                                  }
                                }}
                                component="button"
                                underline="none"
                                sx={backLinkSx}
                              >
                                {t("Back")}
                              </Link>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Container>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <NotAuthorisedPage />
        )}
      </div>

      {/* ERROR TOAST */}
      <Snackbar
        open={errorToast.open}
        autoHideDuration={4000}
        onClose={() => setErrorToast({ ...errorToast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        ContentProps={{
          sx: {
            backgroundColor: "#d32f2f",
            color: "white",
            fontWeight: "bold",
            fontSize: "14px",
          },
        }}
        message={errorToast.message}
      />
    </ThemeProvider>
  );
}
